import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import SlidingMenu, { SlidingMenuOption } from '../general-components/sliding-menu';
import ContentFormProvider from "../../contexts/generated-content/content-form-context";
import ContentGeneratorHandler from "./settings/content-generator-handler";
import AudioSettings from './audio-settings';
import { GreenroomState } from "../../stores/types/types";
import { MEDIA_SETTINGS_TABS } from "../../stores/types/session"
import { TABS } from "../../stores/types/tabs";
import ThemeTab from "./stylepacks/themes/theme-tab";
import { setEditingTheme } from "../../stores/actions/themes";
import { ROOM_STATES } from '../../stores/reducers/settings';
import { setActiveSubTab } from '../../stores/actions/tabs';

const menuOptions: SlidingMenuOption[] = [
	{ label: 'Content', data: { value: MEDIA_SETTINGS_TABS.CONTENT } },
	{ label: 'Style', data: { value: MEDIA_SETTINGS_TABS.THEME } },
	{ label: 'Audio', data: { value: MEDIA_SETTINGS_TABS.AUDIO } },
];


interface MenuProps {
	currentTab: SlidingMenuOption;
	menuCallback: (o: SlidingMenuOption) => void;
	mediaProvider: string | null | undefined;
}
function MediaSettingsSlidingMenu(props: MenuProps) {
	const roomState = useSelector((s: GreenroomState) => s.MeetingReducer.roomState);

	const filteredMenuOptions = React.useMemo(() => {
		if (props.mediaProvider === 'epidemic sound') {
			// has to be epidemic, only shows audio tab
			return menuOptions.filter((o) => o.data.value === MEDIA_SETTINGS_TABS.AUDIO);
		} else if (props.mediaProvider === 'capsule') {
			// its capsule, so shows all tabs
			if (roomState !== ROOM_STATES.EDITING) {
				// unless outside of showbuilder. Then it hides audio (old functionality, unsure why)
				return menuOptions.filter((o) => o.data.value !== MEDIA_SETTINGS_TABS.AUDIO);
			} else {
				return menuOptions;
			}
		} else {
			// no known provider, no known tabs, no sliding menu
			return [];
		}
	}, [roomState, props.mediaProvider]);

	return filteredMenuOptions.length > 1 ? <SlidingMenu
		options={filteredMenuOptions}
		selectedOption={props.currentTab}
		callback={props.menuCallback}
		className="padding-left-small position-relative width-calc-full"
		fullWidthDivider={true}
		dividerClassname='lemonade'
	/> : null;
}

interface Props {
	isForShotEditorModal?: boolean;
}

export default function MediaSettings(props: Props) {
	const { isForShotEditorModal = false } = props;

	const activeSubTabs = useSelector((s: GreenroomState) => s.TabsReducer.activeSubTabs);
	const selectedMediaItem = useSelector((s: GreenroomState) => s.GeneratedContentReducer.selectedMediaItem);

	const dispatch = useDispatch();

	const initialTab = React.useMemo(() => {
		return menuOptions.find((x) => x.data.value === activeSubTabs[TABS.GENERATE_MEDIA_CONTENT]);
	}, [activeSubTabs[TABS.GENERATE_MEDIA_CONTENT]]);

	useEffect(() => {
		setCurrentTab(initialTab ?? menuOptions[0]);
		dispatch(setEditingTheme(null, false));
	}, [initialTab, selectedMediaItem?.id]);

	const [currentTab, setCurrentTab] = useState(initialTab ?? menuOptions[0]);

	const menuCallback = useCallback((o: SlidingMenuOption) => {
		setCurrentTab(o);
		dispatch(setActiveSubTab(TABS.GENERATE_MEDIA_CONTENT, o.data.value));
	}, [])

	return (<>
		<MediaSettingsSlidingMenu
			currentTab={currentTab}
			menuCallback={menuCallback}
			mediaProvider={selectedMediaItem?.provider}
		/>
		<ContentFormProvider isForShotEditorModal={isForShotEditorModal}>
			{currentTab?.data?.value === MEDIA_SETTINGS_TABS.CONTENT && (
					<ContentGeneratorHandler fieldTypes={['image', 'string', 'duration']}/>
				)}
			{currentTab?.data?.value === MEDIA_SETTINGS_TABS.THEME && (
					<ThemeTab isForShotEditorModal={isForShotEditorModal}/>
			)}
			{currentTab?.data?.value === MEDIA_SETTINGS_TABS.AUDIO && (
				<AudioSettings />
			)}
		</ContentFormProvider>
	</>);
}
